import { cx, css as emotionCss, keyframes } from '@emotion/css';
import { useTheme } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import { createContext, useContext, useMemo } from 'react';

const StylesContext = createContext();

export const StylesProvider = ({ children }) => {
  const theme = useTheme();
  const mq = getBreakpoints({ theme });
  const css = (inlineStyle = {}) => createCss(inlineStyle, theme);

  const global = useMemo(() => createGlobalClasses(theme), [theme]);

  return (
    <StylesContext.Provider value={{ theme, mq, css, global }}>
      {children}
    </StylesContext.Provider>
  );
};

StylesProvider.propTypes = {
  children: PropTypes.node,
};

export const useStyles = (styles = {}, opts = {}) => {
  const { css, mq, theme, global } = useContext(StylesContext);
  const classes = createStyles(styles, { mq, ...opts }, theme);
  return { global, classes, css, cx, mq, theme };
};

const getBreakpoints = ({ theme }) =>
  Object.keys(theme.breakpoints.values).reduce(
    (acc, cur) => ({
      ...acc,
      [cur]: `@media(min-width: ${theme.breakpoints.values[cur]}px)`,
    }),
    {}
  );

const isFunction = (fn) => fn instanceof Function;

const createClasses = (styles) =>
  Object.entries(styles).reduce((sheet, [key, value]) => {
    sheet[key] = emotionCss(value);
    return sheet;
  }, {});


  const createGlobalClasses = (theme) => {
    const shorthandFlexClasses = generateFlexUtilityClasses();
    const { typography, ...otherStyles } = theme;
    const typographyClasses = createClasses(typography);

    const globalClasses = createClasses(otherStyles);
    return { ...typographyClasses, ...globalClasses, ...shorthandFlexClasses };
  };

  /*
  * Generate Flex Utility Classes
  * f: flex-direction
  * a: align-items
  * j: justify
  * example: frac = flex-direction: row, align-items: center
  * example 2: frjs = flex-direction: row, justify-content: flex-start
  * example 3: fcasjb = flex-direction: column, align-items: flex-start, justify-content: space-between
  */

  const generateFlexUtilityClasses = () => {
    const flexMap = {
      r: 'row',
      c: 'column',
    };
  
    const alignMap = {
      c: 'center',
      s: 'flex-start',
      e: 'flex-end',
      b: 'baseline',
      s: 'stretch',
    };
  
    const justifyMap = {
      c: 'center',
      s: 'flex-start',
      e: 'flex-end',
      b: 'space-between',
      a: 'space-around',
      v: 'space-evenly',
    };
  
    const utilityClasses = {};
  
    for (const flexKey in flexMap) {
      const baseClass = `f${flexKey}`;
      utilityClasses[baseClass] = {
        display: 'flex',
        flexDirection: flexMap[flexKey],
      };
  
      for (const alignKey in alignMap) {
        const alignClass = `${baseClass}a${alignKey}`;
        utilityClasses[alignClass] = {
          ...utilityClasses[baseClass],
          alignItems: alignMap[alignKey],
        };
  
        for (const justifyKey in justifyMap) {
          const justifyClass = `${baseClass}j${justifyKey}`;
          utilityClasses[justifyClass] = {
            ...utilityClasses[baseClass],
            justifyContent: justifyMap[justifyKey],
          };
  
          const fullClass = `${alignClass}j${justifyKey}`;
          utilityClasses[fullClass] = {
            ...utilityClasses[alignClass],
            justifyContent: justifyMap[justifyKey],
          };
        }
      }
    }
  
    return createClasses(utilityClasses);
  };

const createStyles = (styles, opts, theme) => {
  if (isFunction(styles)) {
    return createClasses(styles(theme, opts));
  }
  return createClasses(styles);
};

const createCss = (styles, theme) => {
  if (isFunction(styles)) {
    return emotionCss(styles(theme));
  }
  return emotionCss(styles);
};

export default useStyles;
export { keyframes };